import { supabase } from './supabase';
import { logNegotiationAction } from './negotiationLogger';
import { resolverAprovacaoDesconto } from './discountApprovalPolicy';
import type { ModalidadePlano } from './politicaComercial';
import { Negociacao, Profile } from '../../types';

/** Permissão que dá direito de aprovar desconto fora da política. Mora aqui (e
 *  não no pop-up) porque a notificação do sino também a usa, e o pop-up já
 *  importa este módulo — importar de volta faria um ciclo. */
export const APROVACAO_DESCONTO_PERMISSION = 'Aprovar desconto fora da política';

/**
 * Nova Política Comercial (jul/2026): a régua de aprovação passa a ser
 * "dentro/fora da política". Plano oficial selecionado (mesmo 28% + 100%
 * bandeira) → aprovado direto; "Fora da política" ou percentuais manuais que
 * não batem com nenhum plano oficial → pendente + notificação aos gerentes.
 *
 * Chamadores legados (UnidadeForm, NegotiationWizard) continuam passando só
 * os percentuais: nesses casos o plano é inferido a partir deles.
 */
/** Como o aviso ao gerente se refere a quem pediu o desconto. */
const ROTULO_SOLICITANTE: Record<ModalidadePlano, string> = {
    vendedor: 'O vendedor',
    parceiro: 'O parceiro',
    vendedor_interno: 'Closer ou BDR —',
};

/**
 * Registro do pedido/decisão em `aprovacoes_desconto` (solicitação 5917).
 *
 * Best-effort de propósito: é um registro A MAIS, ao lado da nota que o fluxo
 * sempre gravou. Falhar aqui não pode derrubar o pedido nem a decisão — o
 * desconto já foi marcado na negociação quando isto roda, e desfazer por causa
 * de um registro de histórico seria pior que ficar sem ele.
 */
async function registrarPedidoAprovacao(dados: {
    negociacaoId: number | string;
    solicitanteId: string;
    solicitanteNome: string | null | undefined;
    modalidade: ModalidadePlano;
    motivo: string;
    tarifa: number;
    bandeira: number;
    carencia: string | null | undefined;
}) {
    const { error } = await supabase.from('aprovacoes_desconto').insert({
        negociacao_id: Number(dados.negociacaoId),
        solicitante_id: dados.solicitanteId,
        solicitante_nome: dados.solicitanteNome || null,
        modalidade: dados.modalidade,
        motivo: dados.motivo,
        desconto_tarifa: dados.tarifa,
        desconto_bandeira: dados.bandeira,
        carencia: dados.carencia || null,
    });
    if (error) console.error('Falha ao registrar pedido em aprovacoes_desconto:', error);
}

/**
 * Fecha o pedido PENDENTE mais recente da negociação. Não cria linha quando não
 * há pedido registrado (pedidos anteriores a 22/09/2026 só existem nas notas):
 * inventar um pedido que não foi gravado seria pior que deixar o antigo sem par.
 */
async function registrarDecisaoAprovacao(
    negociacaoId: number | string,
    status: 'aprovado' | 'rejeitado',
    decisorId: string,
    decisorNome: string | null | undefined,
) {
    const { data: pendente } = await supabase
        .from('aprovacoes_desconto')
        .select('id')
        .eq('negociacao_id', Number(negociacaoId))
        .eq('status', 'pendente')
        .order('solicitado_em', { ascending: false })
        .limit(1)
        .maybeSingle();
    if (!pendente) return;

    const { error } = await supabase
        .from('aprovacoes_desconto')
        .update({
            status,
            decidido_por_id: decisorId,
            decidido_por_nome: decisorNome || null,
            decidido_em: new Date().toISOString(),
        })
        .eq('id', pendente.id);
    if (error) console.error('Falha ao registrar decisão em aprovacoes_desconto:', error);
}

export const checkAndRequestDiscountApproval = async (
    negotiationId: number | string,
    userId: string | null | undefined,
    maxTarifa: number,
    maxBandeira: number,
    fidelity?: string,
    options?: { planoCodigo?: string | null; carencia?: string | null; fidelidadeIsenta?: boolean }
) => {
    if (!userId) return;

    // Busca o perfil do usuário
    const { data: userProfile } = await supabase.from('profiles').select('*').eq('id', userId).single();
    if (!userProfile) return;

    // Quem decide aprovação nunca precisa da própria aprovação — sempre direto.
    // Isto ANTES era "salva aprovado se role != vendedor/parceiro/vendedor_parceiro",
    // o que fazia QUALQUER outro role (inclusive a Júlia Cintra, `analista` do
    // Comercial Interno) escapar da régua por acidente — fechando 45% de desconto
    // sem gerente nenhum ser notificado. Agora o gate é por MODALIDADE (parceiro /
    // vendedor / vendedor_interno via modalidadePorPerfil), que enxerga
    // departamento além de role.
    if (userProfile.role === 'adm' || userProfile.role === 'gerente' || userProfile.role === 'gerente_parcerias') {
        await supabase.from('negociacoes').update({ desconto_status: 'aprovado' }).eq('id', negotiationId);
        return;
    }

    // Decisão pura (testada em src/lib/__tests__/discountApprovalPolicy.test.ts)
    // — carência não passa pela régua no Comercial Interno; vendedor externo
    // segue a régua de jul/2026 (dentro/fora da política + carência).
    const { modalidade, dentroDaPolitica, carenciaSolicitada, fidelidadeIsentaSolicitada, aprovadoDireto } = resolverAprovacaoDesconto({
        perfil: userProfile,
        maxTarifa,
        maxBandeira,
        carencia: options?.carencia,
        planoCodigo: options?.planoCodigo,
        fidelidadeIsenta: options?.fidelidadeIsenta,
    });

    if (aprovadoDireto) {
        await supabase
            .from('negociacoes')
            .update({ desconto_status: 'aprovado' })
            .eq('id', negotiationId);
        return;
    }

    // Fora da política (ou carência solicitada) → fluxo de aprovação
    try {
        const reasons = [];
        if (!dentroDaPolitica) {
            if (modalidade === 'vendedor_interno') {
                reasons.push(`condição fora da política de Closer e BDR (${maxTarifa}% tarifa, ${maxBandeira}% bandeira — faixa aprovada é 20% a 30%)`);
            } else if (modalidade === 'parceiro') {
                reasons.push(`condição especial do parceiro (${maxTarifa}% tarifa, ${maxBandeira}% bandeira — o plano padrão é 20% de tarifa e até 50% de bandeira)`);
            } else if (options?.planoCodigo === 'fora_da_politica') {
                reasons.push(`condição fora da política (${maxTarifa}% tarifa, ${maxBandeira}% bandeira)`);
            } else {
                reasons.push(`percentuais fora dos planos oficiais (${maxTarifa}% tarifa, ${maxBandeira}% bandeira)`);
            }
            if (fidelity) reasons.push(`fidelidade de "${fidelity}"`);
        }
        if (carenciaSolicitada) reasons.push(`carência de "${options!.carencia}"`);
        if (fidelidadeIsentaSolicitada) reasons.push('isento de fidelidade');

        const reasonText = reasons.join(', ');
        // 1. Atualizar o status do desconto na negociação
        const { error: negUpdateError } = await supabase
            .from('negociacoes')
            .update({ desconto_status: 'pendente' })
            .eq('id', negotiationId);

        if (negUpdateError) throw negUpdateError;

        // 2. Criar uma nota especial para aprovação (visível no feed)
        const { error: noteError } = await supabase
            .from('negociacao_notas')
            .insert({
                negociacao_id: negotiationId,
                user_id: userId,
                conteudo: `[APPROVAL_REQUEST] Solicitação de aprovação por: ${reasonText}.`,
                tipo: 'nota'
            });

        await registrarPedidoAprovacao({
            negociacaoId: negotiationId,
            solicitanteId: userId,
            solicitanteNome: userProfile.full_name,
            modalidade,
            motivo: reasonText,
            tarifa: maxTarifa,
            bandeira: maxBandeira,
            carencia: carenciaSolicitada ? options?.carencia : null,
        });

        if (noteError) throw noteError;

        // 3. Notificar os gerentes — MESMO PADRÃO do handleDelete (for loop, um a um)
        const rolesToNotify = ['gerente'];
        if (userProfile.role === 'parceiro' || userProfile.role === 'vendedor_parceiro') {
            rolesToNotify.push('gerente_parcerias');
        }

        const { data: porPapel } = await supabase
            .from('profiles')
            .select('id')
            .in('role', rolesToNotify);

        // Quem tem a permissão de aprovar também recebe o sino (solicitação 5915).
        // O pop-up de aprovação sempre foi por essa permissão, mas o sino era só
        // por papel: o Vinicius Alves (adm), hoje o único que aprova, via o pop-up
        // e nunca recebia o aviso. Só ACRESCENTA destinatários — quem já recebia
        // continua recebendo, e quem pode aprovar não muda.
        const { data: porPermissao } = await supabase
            .from('profiles')
            .select('id')
            .contains('permissions', [APROVACAO_DESCONTO_PERMISSION]);

        // Sem repetir: quem é gerente E tem a permissão recebe um aviso só.
        const managers = [...new Map(
            [...(porPapel || []), ...(porPermissao || [])].map((m) => [m.id, m]),
        ).values()];

        if (managers.length > 0) {
            for (const m of managers) {
                const notificacao = {
                    user_id: m.id,
                    type: 'negotiation_assigned',
                    title: 'Nova Solicitação de Desconto',
                    // Quem pediu é dito pelo perfil, não "O vendedor" fixo — parceiro e
                    // Comercial Interno também pedem, e o gerente decidia sem saber de
                    // qual régua o pedido vinha (solicitação 5918). Só muda o texto:
                    // quem recebe e quem aprova seguem iguais.
                    content: `${ROTULO_SOLICITANTE[modalidade]} ${userProfile.full_name || 'Desconhecido'} solicitou aprovação (${reasonText}) para a negociação #${negotiationId}.`,
                    link: `/crm?negociacaoId=${negotiationId}`,
                    read: false
                };
                const { error: notifError } = await supabase.from('notifications').insert(notificacao);
                if (notifError) console.error(`Erro ao inserir notificação para gerente ${m.id}:`, notifError);
            }
        }

        // 4. Log Action
        await logNegotiationAction(
            Number(negotiationId),
            userId,
            `solicitou aprovação por: ${reasonText}`
        );

        return true;
    } catch (err) {
        console.error("[DiscountApproval] Error:", err);
        return false;
    }
};

/**
 * Aprova/rejeita/transfere uma solicitação de desconto pendente. Compartilhado
 * entre o feed da negociação (DetailFeed) e o popup de aprovação exibido ao
 * gerente/adm/gerente_parcerias ao entrar no sistema — mesma lógica, mesmos
 * efeitos colaterais (nota de auditoria no feed, notificação, refreshKanban),
 * para não haver dois caminhos divergentes de aprovar/rejeitar um desconto.
 */

type NegociacaoParaDecisao = Pick<Negociacao, 'id' | 'cliente_nome' | 'responsavel_id'>;

const buscarSolicitanteId = async (negotiationId: number, fallbackResponsavelId?: string) => {
    const { data: approvalNote } = await supabase
        .from('negociacao_notas')
        .select('user_id')
        .eq('negociacao_id', negotiationId)
        .like('conteudo', '%[APPROVAL_REQUEST]%')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
    return approvalNote?.user_id || fallbackResponsavelId;
};

export const approveDiscount = async (
    negotiation: NegociacaoParaDecisao,
    approverId: string,
    approverName: string | null | undefined
) => {
    const { error } = await supabase.from('negociacoes').update({ desconto_status: 'aprovado' }).eq('id', negotiation.id);
    if (error) throw error;

    const targetUserId = await buscarSolicitanteId(negotiation.id, negotiation.responsavel_id);

    await supabase.from('negociacao_notas').insert({
        negociacao_id: negotiation.id,
        user_id: approverId,
        conteudo: `[DISCOUNT_APPROVED] O gerente ${approverName || 'Gerente'} aprovou o desconto solicitado.`,
        tipo: 'nota'
    });

    await registrarDecisaoAprovacao(negotiation.id, 'aprovado', approverId, approverName);

    if (targetUserId) {
        const { error: notifyError } = await supabase.from('notifications').insert({
            user_id: targetUserId,
            type: 'negotiation_assigned',
            title: 'Desconto Aprovado',
            content: `Seu desconto para a negociação "${negotiation.cliente_nome}" foi aprovado!`,
            link: `/crm?negociacaoId=${negotiation.id}`,
            read: false
        });
        if (notifyError) console.error('Error sending approval notification:', notifyError);
    }

    window.dispatchEvent(new CustomEvent('refreshKanban'));
};

export const rejectDiscount = async (
    negotiation: NegociacaoParaDecisao,
    approverId: string,
    approverName: string | null | undefined
) => {
    const { error } = await supabase.from('negociacoes').update({ desconto_status: 'rejeitado' }).eq('id', negotiation.id);
    if (error) throw error;

    const targetUserId = await buscarSolicitanteId(negotiation.id, negotiation.responsavel_id);

    await supabase.from('negociacao_notas').insert({
        negociacao_id: negotiation.id,
        user_id: approverId,
        conteudo: `[DISCOUNT_REJECTED] O gerente ${approverName || 'Gerente'} rejeitou o desconto solicitado. Reveja os valores do plano.`,
        tipo: 'nota'
    });

    await registrarDecisaoAprovacao(negotiation.id, 'rejeitado', approverId, approverName);

    if (targetUserId) {
        const { error: notifyError } = await supabase.from('notifications').insert({
            user_id: targetUserId,
            type: 'negotiation_assigned',
            title: 'Desconto Rejeitado',
            content: `Seu desconto na negociação "${negotiation.cliente_nome}" foi rejeitado. Reveja os valores do plano.`,
            link: `/crm?negociacaoId=${negotiation.id}`,
            read: false
        });
        if (notifyError) console.error('Error sending rejection notification:', notifyError);
    }

    window.dispatchEvent(new CustomEvent('refreshKanban'));
};

/**
 * Repassa a decisão de uma solicitação pendente para outro gerente/adm —
 * não altera desconto_status nem o responsável (vendedor) da negociação:
 * a fila de aprovação continua visível para qualquer aprovador, isso é só
 * um aviso direcionado (nota no feed + notificação no sino do destino).
 */
export const transferApproval = async (
    negotiation: Pick<Negociacao, 'id' | 'cliente_nome'>,
    fromUserId: string,
    fromUserName: string | null | undefined,
    toProfile: Pick<Profile, 'id' | 'full_name'>
) => {
    await supabase.from('negociacao_notas').insert({
        negociacao_id: negotiation.id,
        user_id: fromUserId,
        conteudo: `[APPROVAL_TRANSFERRED] ${fromUserName || 'Um gerente'} transferiu a aprovação de desconto para ${toProfile.full_name}.`,
        tipo: 'nota'
    });

    const { error: notifyError } = await supabase.from('notifications').insert({
        user_id: toProfile.id,
        type: 'negotiation_assigned',
        title: 'Aprovação de desconto transferida para você',
        content: `${fromUserName || 'Um gerente'} transferiu para você a aprovação do desconto da negociação "${negotiation.cliente_nome}".`,
        link: `/crm?negociacaoId=${negotiation.id}`,
        read: false
    });
    if (notifyError) console.error('Error sending transfer notification:', notifyError);

    await logNegotiationAction(negotiation.id, fromUserId, `transferiu a aprovação de desconto para ${toProfile.full_name}`);
};

/**
 * Adia a revisão de uma pendência para o próprio aprovador que clicou —
 * não é global: outro gerente/adm continua vendo a pendência normalmente.
 * A data-alvo vai embutida na tag (`[APPROVAL_SNOOZE:<ISO>]`) para o popup
 * conseguir filtrar sem precisar de coluna nova; se uma nova solicitação
 * `[APPROVAL_REQUEST]` for criada depois desta nota, ela invalida o adiamento.
 */
export const snoozeApproval = async (
    negotiation: Pick<Negociacao, 'id'>,
    userId: string,
    userName: string | null | undefined,
    dias: number
) => {
    const ate = new Date();
    ate.setDate(ate.getDate() + dias);

    await supabase.from('negociacao_notas').insert({
        negociacao_id: negotiation.id,
        user_id: userId,
        conteudo: `[APPROVAL_SNOOZE:${ate.toISOString()}] ${userName || 'Um gerente'} adiou a revisão deste desconto por ${dias} dia${dias > 1 ? 's' : ''} — volta a aparecer para ele em ${ate.toLocaleDateString('pt-BR')}.`,
        tipo: 'nota'
    });

    await logNegotiationAction(negotiation.id, userId, `adiou a revisão do desconto por ${dias} dia${dias > 1 ? 's' : ''}`);
};
